<template>
  <div class="modal fade" id="buttonLinkModal" tabindex="-1" aria-labelledby="buttonLinkModalLabel"
    :aria-hidden="!isModalVisible">
    <div class="modal-dialog custom-modal-dialog">
      <div class="modal-content border-0 shadow-lg">

        <div class="modal-header bg-white border-bottom-0 pb-0">
          <div>
            <h5 class="modal-title fw-bold text-primary mb-1" id="buttonLinkModalLabel">
              <i :class="isEditing ? 'bi bi-pencil-square' : 'bi bi-plus-circle'" class="me-2"></i>
              {{ isEditing ? "Editar Botón" : "Nuevo Botón" }}
            </h5>
            <p class="text-muted small mb-0">
              Paso {{ currentStep }} de 2:
              {{ currentStep === 1 ? 'Configuración General' : 'Contenido y Destino' }}
            </p>
          </div>
          <button type="button" class="btn-close" @click="closeForm" aria-label="Close"></button>
        </div>

        <div class="modal-body pt-4">
          <form @submit.prevent="submitForm">

            <div v-if="currentStep === 1" class="fade-in">
              <h6 class="text-muted border-bottom pb-2 mb-4 small fw-bold text-uppercase">
                Configuración Inicial
              </h6>

              <div class="row g-3 mb-4">
                <div class="col-12">
                  <div class="form-floating">
                    <select v-model.number="form.type" class="form-select" id="type-select">
                      <option v-for="type in buttonLinkTypes" :key="type.value" :value="type.value">
                        {{ type.label }}
                      </option>
                    </select>
                    <label for="type-select">Tipo de Elemento</label>
                  </div>
                </div>

                <div class="col-12">
                  <ToggleField label="Título Visible" id="title" v-model="form.title" type="input"
                    @toggle="onToggleTitle" @blur="onTitleUpdated">
                    <template #info>
                      <i class="bi bi-info-circle-fill text-primary cursor-pointer ms-2"
                        @click.stop="showHelp(form.type === 3 ? 'title_banner' : 'title')" title="Ver ayuda"></i>
                    </template>
                  </ToggleField>
                </div>

                <div class="col-md-6">
                  <label for="color" class="form-label fw-bold small text-uppercase text-muted mb-1">Color de
                    Fondo</label>
                  <div class="d-flex align-items-center p-1 border rounded bg-white" style="height: 58px;">
                    <input type="color" v-model="form.background_color"
                      class="form-control form-control-color custom-color-input me-3 border-0 bg-transparent" id="color"
                      title="Elige tu color" :disabled="foundBackgroundColor !== null" required />
                    <span class="text-muted small font-monospace">{{ form.background_color }}</span>
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="order" class="form-label fw-bold small text-uppercase text-muted mb-1">Orden</label>
                  <input type="number" v-model="form.order" class="form-control" id="order" placeholder="0" required
                    style="height: 58px;" />
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4 pt-3 border-top">
                <button type="button" class="btn btn-primary px-5 rounded-pill shadow-sm" @click="nextStep"
                  :disabled="!isStep1Complete">
                  Siguiente <i class="bi bi-chevron-right ms-1"></i>
                </button>
              </div>
            </div>

            <div v-if="currentStep === 2" class="fade-in">
              <h6 class="text-muted border-bottom pb-2 mb-4 small fw-bold text-uppercase">
                Detalles de Contenido
              </h6>

              <div class="row g-3 mb-4">
                <div class="col-12">
                  <div class="form-floating position-relative">
                    <input type="text" v-model="form.name" class="form-control pe-5" id="name"
                      placeholder="Nombre Interno" />
                    <label for="name">Nombre Interno</label>
                    <i class="bi bi-info-circle-fill text-primary cursor-pointer position-absolute top-50 end-0 translate-middle-y me-3 fs-5"
                      @click="showHelp(form.type === 3 ? 'name_banner' : 'name')" title="Ayuda sobre Nombre"
                      style="z-index: 5;"></i>
                  </div>
                </div>

                <div v-if="isLoading" class="col-12 text-center">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span class="small ms-2 text-muted">Validando...</span>
                </div>

                <div class="col-12" :class="showButtonText ? 'col-md-6' : 'col-md-12'">
                  <div class="form-floating position-relative">
                    <textarea v-model="form.content" class="form-control pe-5" id="content" placeholder="Subtítulo"
                      :disabled="isContentLocked" style="height: 58px"></textarea>
                    <label for="content">Subtítulo / Descripción</label>
                    <i class="bi bi-info-circle-fill text-primary cursor-pointer position-absolute top-0 end-0 mt-3 me-3 fs-5"
                      @click="showHelp(form.type === 3 ? 'subtitulo_banner' : 'subtitulo_other')"
                      title="Ayuda sobre Subtítulo" style="z-index: 5;"></i>
                  </div>
                </div>

                <div class="col-12 col-md-6" v-if="showButtonText">
                  <div class="form-floating position-relative">
                    <input type="text" v-model="form.button_text" class="form-control" id="button_text"
                      placeholder="Ej: Ver más" />
                    <label for="button_text">Texto Botón (Opcional)</label>
                  </div>
                </div>
              </div>

              <div class="p-4 bg-light rounded-3 border mb-4"
                v-if="form.type === 1 || form.type === 4 || form.type === 2 || form.type === 5">
                <div class="text-center mb-3" v-if="form.type !== 5">
                  <label class="form-label text-uppercase text-muted fw-bold small tracking-wide">
                    Configuración de Destino
                  </label>
                  <div class="d-flex justify-content-center">
                    <div class="btn-group shadow-sm bg-white rounded" role="group" style="min-width: 300px;">
                      <input type="radio" class="btn-check" name="linkType" id="linkType1" value="link"
                        v-model="form.linkType">
                      <label class="btn btn-outline-primary px-4 py-2" for="linkType1">
                        <i class="bi bi-link-45deg fs-5 align-middle"></i> Enlace
                      </label>

                      <input type="radio" class="btn-check" name="linkType" id="linkType2" value="pdf"
                        v-model="form.linkType">
                      <label class="btn btn-outline-primary px-4 py-2" for="linkType2">
                        <i class="bi bi-file-earmark-pdf fs-5 align-middle"></i> PDF
                      </label>
                    </div>
                  </div>
                </div>

                <div class="row" v-if="form.linkType === 'link'">
                  <div class="col-12">
                    <div class="form-floating">
                      <input type="text" v-model="form.link" class="form-control bg-white" id="link"
                        placeholder="https://" />
                      <label for="link" class="text-muted">URL de redirección</label>
                    </div>
                  </div>
                </div>

                <div class="row" v-if="form.linkType === 'pdf' && form.type !== 5">
                  <div class="col-12">
                    <FileUploadPreview label="Subir PDF" id="file" accept="application/pdf" :file="form.file"
                      :preview="getFilePreview(form.file)" :required="!isEditing" :view-id="form.viewId"
                      @file-change="updateFile('file', $event)" no-file-message="Ningún archivo seleccionado"
                      class="clean-upload" />
                  </div>
                </div>

                <div class="row" v-if="form.type === 5 && isEditing">
                  <div class="col-12">
                    <label class="form-label fw-bold small text-uppercase text-muted mb-2">Archivos Adjuntos</label>
                    <FileUploadPreviewMultiple label="Gestión de PDFs" id="files" accept="application/pdf"
                      :initial-files="form.files" :view-id="form.button_link_id" :required="!isEditing"
                      @file-change="updateFile('files', $event)" @file-removed="handleFileRemoved"
                      no-file-message="No se han seleccionado archivos" />
                  </div>
                </div>
              </div>

              <h6 class="text-muted border-bottom pb-2 mb-3 small fw-bold text-uppercase">Multimedia</h6>
              <div class="row g-3">
                <div class="col-12">
                  <FileUploadPreview accept="image/*" label="Imagen de Referencia" id="image" :file="form.image"
                    :preview="form.imagePreview" @file-change="updateFile('image', $event)"
                    no-file-message="Sin imagen seleccionada" class="clean-upload" />
                </div>
              </div>

              <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                <button type="button" class="btn btn-outline-secondary px-4 rounded-pill" @click="prevStep">
                  <i class="bi bi-chevron-left me-1"></i> Anterior
                </button>
                <button type="submit" class="btn btn-success px-5 rounded-pill shadow-sm" :disabled="!isFormValid">
                  <i class="bi bi-check-lg me-1"></i> {{ isEditing ? "Actualizar" : "Guardar" }}
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>
    </div>

    <HelpModal v-if="helpField" :field="helpField" @close="helpField = null" />
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from "vue";
import { Modal } from "bootstrap";
import {
  createButtonLink,
  updateButtonLink,
  fetchButtonLinksByTitle,
} from "@/services/api/buttonLinkService.js";
import FileUploadPreview from "@/components/shared/FileUploadPreview.vue";
import FileUploadPreviewMultiple from "@/components/shared/FileUploadPreviewMultiple.vue";
import Swal from "sweetalert2";
import HelpModal from "@/components/shared/HelpModal.vue";
import { buildFormData } from "@/utils/formDataBuilder.js";
import { showLoadingAlert, closeLoadingAlert } from "@/utils/loadingAlert.js";
import ToggleField from "@/components/shared/ToggleField.vue";

const BUTTON_LINK_MODAL_ID = "buttonLinkModal";

export default {
  components: {
    FileUploadPreview,
    FileUploadPreviewMultiple,
    HelpModal,
    ToggleField,
  },
  props: {
    view: { type: Object, required: true },
    header: { type: Object, default: null },
    buttonLinkTypes: { type: Array, required: true },
    foundBackgroundColor: { type: String, default: null },
  },
  setup(props, { emit }) {
    const isModalVisible = ref(false);
    const isLoading = ref(false);
    const helpField = ref(null);
    const isEditing = ref(false);
    const currentStep = ref(1);
    const isContentLocked = ref(false);
    const originalBackgroundColor = ref(null);

    const isHydrating = ref(false);

    const form = ref({
      name: "",
      title: "",
      content: "",
      button_text: "",
      link: "",
      file: null,
      image: null,
      files: [],
      order: "",
      view_id: props.view.id,
      viewId: null,
      button_link_id: null,
      imagePreview: "",
      linkType: "link",
      background_color: "#FEFBF9",
      type: 1,
    });

    const getFilePreview = (file) => {
      if (typeof file === "string") {
        return file; 
      }
      return "";
    };

    const showButtonText = computed(() => {
      return form.value.type !== 1 && form.value.type !== 3;
    });

    const isStep1Complete = computed(() => {
      const typeValid = form.value.type !== null;
      const titleValid = form.value.type === 1 || form.value.title?.trim() !== "" || form.value.type === 5;
      const orderValid = form.value.order !== "" && !isNaN(form.value.order);
      return typeValid && titleValid && orderValid;
    });

    const isFormValid = computed(() => {
      const { name, link, file, files, linkType, type } = form.value;
      const isNameValid = [4, 5].includes(type) ? true : name && name.trim() !== "";
      let isLinkOrFileValid = false;
      if (type === 3) {
        isLinkOrFileValid = true;
      } else if (linkType === "pdf") {
        if (type === 5) {
          isLinkOrFileValid = isEditing.value ? (files && files.length > 0) : true;
        } else {
          isLinkOrFileValid = file && (typeof file === "string" ? file.trim() !== "" : file.name && file.name.trim() !== "");
        }
      } else if (linkType === "link") {
        isLinkOrFileValid = link && link.trim() !== "";
      }
      return isNameValid && isLinkOrFileValid;
    });

    const searchButtonLinkByTitle = async ({ title, view_id, type }) => {
      try {
        const buttonLinks = await fetchButtonLinksByTitle(view_id, title, type);
        if (Array.isArray(buttonLinks) && buttonLinks.length > 0) {
          const firstButtonLink = buttonLinks[0];
          if (firstButtonLink && firstButtonLink.content) {
            form.value.content = firstButtonLink.content;
            isContentLocked.value = true;
          } else {
            form.value.content = "";
            isContentLocked.value = false;
          }
        } else {
          form.value.content = "";
          isContentLocked.value = false;
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          isContentLocked.value = false;
          form.value.content = "";
        } else {
          console.error("Error inesperado:", error);
        }
      }
    };

    const nextStep = () => { currentStep.value = 2; };
    const prevStep = () => { currentStep.value = 1; };
    const showHelp = (field) => { helpField.value = field; };

    watch(() => form.value.linkType, (newLinkType) => {
      if (newLinkType === "link") {
        form.value.files = [];
        form.value.file = null;
      } else if (newLinkType === "pdf") {
        form.value.link = "";
      }
    });

    watch(() => form.value.type, (newType) => {
      if (isHydrating.value) return;

      if (newType === 5) {
        form.value.linkType = "pdf";
        form.value.file = null;
      } else {
        form.value.linkType = "link";
        form.value.files = [];
      }
      if (newType === 1 || newType === 3) {
        form.value.button_text = "";
      }
    }, { immediate: true });

    watch(
      () => ({ type: form.value.type, title: form.value.title }),
      ({ type, title }) => {
        if (type === 3 || type === 4) {
          if (!isEditing.value) {
            form.value.background_color = "#101b5a";
          } else if (isEditing.value && originalBackgroundColor.value === null) {
            form.value.background_color = "#101b5a";
          }
        }
        emit("fetchButtonColor", type, title);
      }
    );

    watch(() => props.foundBackgroundColor, (newColor) => {
      if (newColor) form.value.background_color = newColor;
    });

    watch(
      () => props.header,
      async (newHeader) => {
        if (newHeader) {
          isHydrating.value = true;
          isEditing.value = true;
          originalBackgroundColor.value = newHeader.background_color || null;
          form.value = {
            name: newHeader.name,
            title: (typeof newHeader.title === "string" && newHeader.title?.toLowerCase() === "no title") ? "" : newHeader.title || "",
            content: newHeader.content,
            button_text: newHeader.button_text || "",
            link: newHeader.link,
            image: null,
            file: newHeader.file || null,
            order: newHeader.order,
            view_id: newHeader.view.id,
            viewId: newHeader.id,
            button_link_id: newHeader.id,
            imagePreview: newHeader.image,
            linkType: newHeader.file || (newHeader.button_files && newHeader.button_files.length > 0) ? "pdf" : (newHeader.link ? "link" : "link"),
            background_color: newHeader.background_color || "#FEFBF9",
            type: newHeader.type || 1,
          };

          console.log("Formulario cargado para editar (Raw):", newHeader);
          console.log("Formulario procesado (Form):", JSON.parse(JSON.stringify(form.value)));
          if (newHeader.button_files) {
            form.value.files = newHeader.button_files.map((file) => ({
              id: file.id,
              file: file.file,
              name: file.name,
              extension: file.extension,
              url: file.url || null,
              order: file.order,
              button_link_id: file.button_link_id,
              created_at: file.created_at,
              updated_at: file.updated_at,
            }));
          }
          if (form.value.type === 3 || form.value.type === 4) {
            await searchButtonLinkByTitle({
              title: form.value.title,
              view_id: form.value.view_id,
              type: form.value.type,
            });
          }
          setTimeout(() => {
            isHydrating.value = false;
          }, 0);
        } else {
          isEditing.value = false;
          originalBackgroundColor.value = null;
          form.value = {
            name: "",
            title: "",
            content: "",
            button_text: "",
            link: "",
            file: null,
            image: null,
            files: [],
            order: "",
            view_id: props.view.id,
            viewId: null,
            button_link_id: null,
            imagePreview: "",
            linkType: "link",
            background_color: "#FEFBF9",
            type: 1,
          };
        }
      },
      { immediate: true }
    );

    const onToggleTitle = (value) => { if (!value) form.value.title = ""; };

    const onTitleUpdated = async () => {
      if (form.value.type === 3 || form.value.type === 4) {
        isLoading.value = true;
        try {
          await searchButtonLinkByTitle({
            title: form.value.title,
            view_id: form.value.view_id,
            type: form.value.type,
          });
        } finally {
          isLoading.value = false;
        }
      }
    };

    const updateFile = (type, file) => {
      form.value[type] = file;
      if (type === 'files') emit('buttonLink-created');
    };

    const handleFileRemoved = (file) => {
      const updatedFiles = form.value.files.filter((f) => f.file !== file.file);
      form.value.files = [...updatedFiles];
      emit('buttonLink-created');
    };

    const submitForm = async () => {
      if (form.value.type !== 3) {
        if (form.value.linkType === "link") form.value.file = null;
        else if (form.value.linkType === "pdf") form.value.link = null;
      }
      if (form.value.linkType === "pdf" && isEditing.value && props.header?.id) {
        form.value.viewId = props.header.id;
        form.value.button_link_id = props.header.id;
      }
      const formData = buildFormData(form.value, form.value.linkType);

      const editOrCreate = isEditing.value ? "editar" : "crear";
      const result = await Swal.fire({
        title: 'Confirmación',
        text: `¿Está seguro de ${editOrCreate} el button?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        backdrop: true,
        customClass: { container: 'swal2-container-custom' }
      });

      if (!result.isConfirmed) return;
      showLoadingAlert();
      try {
        if (isEditing.value) {
          await updateButtonLink(props.header.id, formData);
          closeLoadingAlert();
          await Swal.fire({ icon: 'success', title: 'Éxito', text: 'Se ha actualizado correctamente' });
        } else {
          await createButtonLink(formData);
          closeLoadingAlert();
          await Swal.fire({ icon: 'success', title: 'Éxito', text: 'Se ha creado correctamente' });
        }

        emit("buttonLink-created");
        closeForm();
      } catch (error) {
        closeLoadingAlert();
        emit("error", error.response?.data?.message || "Error al procesar la solicitud.");
      }
    };

    const closeForm = () => {
      emit("close");
      const modalElement = document.getElementById(BUTTON_LINK_MODAL_ID);
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) modalInstance.hide();
    };

    onMounted(() => {
      const modalElement = document.getElementById(BUTTON_LINK_MODAL_ID);
      const modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      modalInstance.show();
      isModalVisible.value = true;
    });

    return {
      handleFileRemoved,
      onToggleTitle,
      form,
      isEditing,
      updateFile,
      submitForm,
      closeForm,
      nextStep,
      prevStep,
      currentStep,
      showHelp,
      onTitleUpdated,
      helpField,
      isContentLocked,
      isLoading,
      isFormValid,
      isStep1Complete,
      isModalVisible,
      showButtonText,
      getFilePreview,
    };
  },
};
</script>

<style scoped>
.custom-modal-dialog {
  max-width: 700px;
  margin: 1.75rem auto;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.custom-color-input {
  width: 60px;
  height: 40px;
  padding: 2px;
  border-radius: 8px;
  border: 1px solid #ced4da;
  cursor: pointer;
}

.cursor-pointer {
  cursor: pointer;
}

::v-deep .clean-upload .border {
  background-color: #f8f9fa !important;
  border: 2px dashed #dee2e6 !important;
  color: #6c757d !important;
  transition: all 0.3s;
}

::v-deep .clean-upload .border:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

:deep(.swal2-container) {
  z-index: 1060 !important;
}

@media (max-width: 768px) {
  .custom-modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }
}
</style>